import type { CreditNoteResponse } from '@palaccount/contracts';
import { Money } from '@palaccount/money';

import type { TrackingPayload } from './tracking-capture';

/**
 * BR-SAL-05-03's quantity cap, mirrored for the return editor: the returned
 * quantity on a line must not exceed the billed quantity minus what earlier
 * (posted) returns already took back. The server and the DB posting gate
 * enforce the cap regardless — this mirror exists so the editor can refuse
 * and explain BEFORE the round trip, with the same numbers the server would
 * use.
 */

export interface ReturnableLine {
  billedQty: string;
  alreadyReturnedQty: string;
}

/** billed − already returned, as a decimal string (never negative). */
export function remainingQty(line: ReturnableLine, currency = 'USD'): string {
  const left = Money.of(line.billedQty, currency).minus(
    Money.of(line.alreadyReturnedQty, currency),
  );
  return left.isNegative() ? '0' : left.toDecimalString();
}

/** Whether `requested` fits under the BR-SAL-05-03 cap. Empty/invalid → false. */
export function fitsCap(requested: string, line: ReturnableLine, currency = 'USD'): boolean {
  const value = requested.trim();
  if (!/^\d+(\.\d+)?$/.test(value)) return false;
  const qty = Money.of(value, currency);
  if (!qty.isPositive()) return false;
  return !qty.greaterThan(Money.of(remainingQty(line, currency), currency));
}

/**
 * The editor's returning set → the wire's lines array: only rows with a
 * positive quantity ride; zero/blank rows mean "not coming back".
 *
 * `tracking` is whatever BR-MD-03-17 needs for that row — nothing at all for
 * an untracked item, the serials or the lot coming back for a tracked one,
 * and one block per tracked PART when the line is an auto-deducting kit (R7
 * in reverse: it went out as its parts, it comes back as them).
 */
export function toRequestLines<T extends TrackingPayload>(
  rows: readonly { invoiceLineNo: number; qty: string; tracking?: T }[],
): ({ invoiceLineNo: number; qty: string } & TrackingPayload)[] {
  return rows
    .map((row) => ({
      invoiceLineNo: row.invoiceLineNo,
      qty: row.qty.trim(),
      ...(row.tracking ?? {}),
    }))
    .filter((row) => /^\d+(\.\d+)?$/.test(row.qty) && !/^0+(\.0+)?$/.test(row.qty));
}

/** '12.000' → '12', '4.500' → '4.5' — quantities read like the spec writes them. */
export function trimQty(value: string): string {
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}

/** A posted credit note is locked except its notes (R8 / BR-SAL-00-22). */
export function isReturnEditable(status: CreditNoteResponse['status']): boolean {
  return status === 'draft';
}
